import React from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Auth0Provider } from '@auth0/auth0-react'
import HomeLayout from './components/layout/HomeLayout'
import AppLayout from './components/layout/AppLayout'
import { NotificationProvider } from './context/NotificationContext'
import NotificationContainer from './components/shared/NotificationContainer'
import ProtectedRoute from './components/shared/ProtectedRoute'
import { useTokenManager } from './hooks/useTokenManager'
import { usePostHogIdentify } from './hooks/usePostHogIdentify'

import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import PortfolioPage from './pages/PortfolioPage'
import PortfolioCompanyDetailPage from './pages/PortfolioCompanyDetailPage'
import UpdateViewPage from './pages/UpdateViewPage'
import SettingsPage from './pages/SettingsPage'
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage'
import TermsOfServicePage from './pages/legal/TermsOfServicePage'
import DisclaimerPage from './pages/legal/DisclaimerPage'
import NotFoundPage from './pages/NotFoundPage'



// Component to initialize token manager and PostHog identification
const AppContent = () => {
  useTokenManager() // Initialize token manager
  usePostHogIdentify() // Identify user in PostHog when authenticated
  
  return (
    <NotificationProvider>
        <Routes>
          {/* Public pages - simplified layout */}
          <Route path="/" element={
            <HomeLayout>
              <HomePage />
            </HomeLayout>
          } />
          <Route path="/login" element={<LoginPage />} />
          
          {/* All other pages use AppLayout and are protected */}
          <Route path="/privacy" element={
            <ProtectedRoute>
              <AppLayout>
                <PrivacyPolicyPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/terms" element={
            <ProtectedRoute>
              <AppLayout>
                <TermsOfServicePage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/disclaimer" element={
            <ProtectedRoute>
              <AppLayout>
                <DisclaimerPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <AppLayout>
                <DashboardPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/portfolio" element={
            <ProtectedRoute>
              <AppLayout>
                <PortfolioPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/portfolio/:startupId" element={
            <ProtectedRoute>
              <AppLayout>
                <PortfolioCompanyDetailPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/portfolio/:startupId/updates/:updateId" element={
            <ProtectedRoute>
              <AppLayout>
                <UpdateViewPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <AppLayout>
                <SettingsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          {/* Catch-all route for 404 pages - must be last */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        
        {/* Global notification container */}
        <NotificationContainer />
    </NotificationProvider>
  )
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email'
        }}
      >
        <AppContent />
      </Auth0Provider>
    </Router>
  )
}

export default App
